import { useState, useRef, useEffect, type KeyboardEvent } from "react";
import { ArrowUp, Paperclip, Plus, X, Mic, Square, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getValidAccessTokenFromStorage } from "@weco/suite-client";
import { useVoiceRecorder } from "../hooks/useVoiceRecorder";
import { useVoiceEnabled } from "../hooks/useVoiceEnabled";
import { transcribeAudio } from "../api/voiceApi";
import { useVoiceMoodPublisher } from "../../../components/mochi/publishers";

interface AttachmentChip {
  id: string;
  name: string;
}

interface ComposerBarProps {
  onSubmit: (text: string, attachmentId?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Pre-fill the textarea with this value (e.g., from a suggestion chip). */
  initialValue?: string;
}

function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function ComposerBar({
  onSubmit,
  disabled = false,
  placeholder,
  initialValue,
}: ComposerBarProps) {
  const { t, i18n } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t("assistant.composerPlaceholder");
  const [value, setValue] = useState(initialValue ?? "");
  const [attachment, setAttachment] = useState<AttachmentChip | null>(null);
  const [uploading, setUploading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { voiceEnabled } = useVoiceEnabled();
  const recorder = useVoiceRecorder();
  useVoiceMoodPublisher(recorder.status);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  }, [value]);

  // Surface a mic-permission failure as a dismissable notice.
  useEffect(() => {
    if (recorder.error === "blocked") setVoiceNotice(t("assistant.voice.blocked"));
    else if (recorder.error === "no-device") setVoiceNotice(t("assistant.voice.noDevice"));
    else if (recorder.error === "unknown") setVoiceNotice(t("assistant.voice.micError"));
  }, [recorder.error, t]);

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    onSubmit(text, attachment?.id);
    setValue("");
    setAttachment(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setVoiceNotice(null);
    try {
      const token = await getValidAccessTokenFromStorage();
      const form = new FormData();
      form.append("file", file);
      const resp = await fetch("/api/assistant/attachments/", {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!resp.ok) {
        let detail = "";
        try {
          const err = await resp.json();
          if (err && typeof err.error === "string") detail = err.error;
        } catch {
          /* non-JSON error body */
        }
        setVoiceNotice(detail || t("assistant.upload.failed"));
        return;
      }
      const data = await resp.json();
      setAttachment({ id: data.attachment_id, name: data.file_name });
    } catch {
      setVoiceNotice(t("assistant.upload.failed"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const startRecording = async () => {
    setVoiceNotice(null);
    await recorder.start();
  };

  const cancelRecording = () => {
    recorder.cancel();
  };

  const stopAndTranscribe = async () => {
    const blob = await recorder.stop();
    if (!blob) return;
    setTranscribing(true);
    setVoiceNotice(null);
    try {
      const lang = (i18n.language || "en").split("-")[0];
      const text = (await transcribeAudio(blob, lang)).trim();
      if (!text) {
        setVoiceNotice(t("assistant.voice.empty"));
        return;
      }
      setValue((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
      textareaRef.current?.focus();
    } catch (err) {
      const msg = err instanceof Error && err.message ? err.message : t("assistant.voice.failed");
      setVoiceNotice(msg);
    } finally {
      setTranscribing(false);
    }
  };

  const isRecording = recorder.status === "recording" || recorder.status === "requesting";
  const composerBusy = disabled || transcribing;

  return (
    <div className="w-full shrink-0">
      {attachment && (
        <div className="mb-2 flex items-center gap-2">
          <span className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground border border-border">
            <Paperclip className="size-3" />
            {attachment.name}
            <button
              type="button"
              onClick={() => setAttachment(null)}
              className="ml-1 hover:text-red-500"
              aria-label="Remove attachment"
            >
              <X className="size-3" />
            </button>
          </span>
        </div>
      )}
      {voiceNotice && (
        <div className="mb-2 flex items-center gap-2">
          <span className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground border border-border">
            {voiceNotice}
            <button
              type="button"
              onClick={() => setVoiceNotice(null)}
              className="ml-1 hover:text-foreground"
              aria-label={t("assistant.voice.cancel")}
            >
              <X className="size-3" />
            </button>
          </span>
        </div>
      )}
      <div className="w-full rounded-2xl border border-border bg-background shadow-sm hover:shadow-md transition-shadow flex flex-col focus-within:ring-1 focus-within:ring-border">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          disabled={composerBusy}
          aria-label="Message input"
          className="w-full bg-transparent px-4 pt-4 pb-2 text-[15px] resize-none outline-none min-h-[100px] placeholder:text-muted-foreground/80 font-medium text-foreground"
          style={{ maxHeight: 200 }}
        />
        <div className="flex flex-col gap-2 px-3 py-2 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <button
                type="button"
                title="Attach file"
                disabled={composerBusy || uploading || isRecording}
                onClick={() => fileInputRef.current?.click()}
                className="text-foreground hover:text-foreground w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted disabled:opacity-50 transition-colors"
              >
                <Plus className="size-5" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.txt,.csv,.md,.json,.xlsx,.xls"
                className="sr-only"
                onChange={handleFileChange}
              />

              {voiceEnabled && !isRecording && (
                <button
                  type="button"
                  title={transcribing ? t("assistant.voice.transcribing") : t("assistant.voice.start")}
                  aria-label={transcribing ? t("assistant.voice.transcribing") : t("assistant.voice.start")}
                  disabled={composerBusy}
                  onClick={startRecording}
                  className="text-foreground hover:text-foreground w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted disabled:opacity-50 transition-colors"
                >
                  {transcribing ? (
                    <Loader2 className="size-5 animate-spin" />
                  ) : (
                    <Mic className="size-5" />
                  )}
                </button>
              )}

              {voiceEnabled && isRecording && (
                <div className="flex items-center gap-1">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-red-500 px-1">
                    <span className="inline-block size-2 rounded-full bg-red-500 animate-pulse" />
                    {formatDuration(recorder.durationMs)}
                  </span>
                  <button
                    type="button"
                    title={t("assistant.voice.stop")}
                    aria-label={t("assistant.voice.stop")}
                    onClick={stopAndTranscribe}
                    className="text-white bg-red-500 hover:bg-red-600 w-8 h-8 flex items-center justify-center rounded-full transition-colors"
                  >
                    <Square className="size-3.5" fill="currentColor" />
                  </button>
                  <button
                    type="button"
                    title={t("assistant.voice.cancel")}
                    aria-label={t("assistant.voice.cancel")}
                    onClick={cancelRecording}
                    className="text-foreground hover:text-foreground w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                  >
                    <X className="size-5" />
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={composerBusy || !value.trim()}
              aria-label="Send message"
              className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                value.trim() && !composerBusy
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-muted text-foreground"
              }`}
            >
              <ArrowUp className="size-4" strokeWidth={3} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
